import { faEye, faPen, faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { OIInterestIncomeDetailsForm } from ".";
import { IFormITR1, IInterestIncomes, IReviewOSIncomes } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const OIInterestIncomeDetails: React.FC = () => {
  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<IInterestIncomes | null>(null);
  const [deleteMsg, setDeleteMsg] = useState<string>("");

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
  const InterestIncomesDetails = values?.IncomeDetails?.InterestIncomes;

  const hasNonEmptyValues = (incomeData: IInterestIncomes) => {
    const {
      BondInvestments,
      DepositIncomes,
      IncomeTaxRefundInterestAmt,
      OtherInterestIncomes,
      P2PInvestments,
      PFIncomes,
      SavingBankIncomes
    } = incomeData;

    const valuesToCheck = [
      ...(SavingBankIncomes[0] || []),
      ...(DepositIncomes[0] || []),
      ...(P2PInvestments[0] || []),
      ...(BondInvestments[0] || []),
      ...(PFIncomes[0] || []),
      ...(IncomeTaxRefundInterestAmt[0] || []),
      ...(OtherInterestIncomes[0] || [])
    ];

    return valuesToCheck.some((value) => value !== "");
  };

  const handleTotalCalculation = (incomeData: IInterestIncomes) => {
    let TotalInterestIncome = 0;
    const InterestIncomes = Object.values(incomeData || {});
    InterestIncomes.forEach((incomes) => {
      if (incomes.length) {
        incomes.forEach((item: any) => {
          if (item.length && item[1]) {
            TotalInterestIncome += Number(item[1]);
          }
        });
      }
    });

    const OtherSourcesIncomes: IReviewOSIncomes = {
      ...values.OthersInc,
      TotalInterestIncome: TotalInterestIncome
    };
    const TotalIncomeOthSrc = Object.values(OtherSourcesIncomes)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const DATA = {
      OthersInc: OtherSourcesIncomes,
      IncomeOthSrc: TotalIncomeOthSrc
    };

    setValues({ ...cloneDeep(values), ...DATA });
    if (hasNonEmptyValues(incomeData)) {
      setFieldValue("IncomeDetails.InterestIncomes", incomeData);
    } else {
      setFieldValue("IncomeDetails.InterestIncomes", null);
    }
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>
            Interest Income
            <small>Any interest earned from saving banks, deposits, income tax refund etc.</small>
          </h5>
          <PreventEditComponent>
            {InterestIncomesDetails ? (
              <Button
                variant="warning"
                size="sm"
                onClick={() => {
                  setShowAddFormCanvas(() => true);
                }}
              >
                <FASvgIcon icon={faPen} />
                Edit
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowAddFormCanvas(() => true);
                }}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            )}
          </PreventEditComponent>

          {isFilingReadOnly && (
            <Button
              variant="warning"
              size="sm"
              className="text-white"
              onClick={() => setShowAddFormCanvas(() => true)}
            >
              <FASvgIcon icon={faEye} />
              View
            </Button>
          )}
        </div>

        {InterestIncomesDetails ? (
          <dl className="eds-list eds-dl eds-inline-between">
            <RenderListItem
              title="Interest from savings bank account"
              incomes={InterestIncomesDetails?.SavingBankIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest from savings bank account");
                setShowDeleteModal({
                  ...InterestIncomesDetails,
                  SavingBankIncomes: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Interest from Deposits"
              incomes={InterestIncomesDetails?.DepositIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest from Deposits");
                setShowDeleteModal({
                  ...InterestIncomesDetails,
                  DepositIncomes: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Interest Income on PF"
              incomes={InterestIncomesDetails?.PFIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest Income on PF");
                setShowDeleteModal({
                  ...InterestIncomesDetails,
                  PFIncomes: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Interest on Income Tax Refund"
              incomes={InterestIncomesDetails?.IncomeTaxRefundInterestAmt}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest on Income Tax Refund");
                setShowDeleteModal({
                  ...InterestIncomesDetails,
                  IncomeTaxRefundInterestAmt: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Any Other Interest Income"
              incomes={InterestIncomesDetails?.OtherInterestIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Any Other Interest Income");
                setShowDeleteModal({
                  ...InterestIncomesDetails,
                  OtherInterestIncomes: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Interest earned from P2P investments"
              incomes={InterestIncomesDetails?.P2PInvestments}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest earned from P2P investments");
                setShowDeleteModal({
                  ...InterestIncomesDetails,
                  P2PInvestments: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Interest earned from bond investments"
              incomes={InterestIncomesDetails?.BondInvestments}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest earned from bond investments");
                setShowDeleteModal({
                  ...InterestIncomesDetails,
                  BondInvestments: [["", ""]]
                });
              }}
            />
          </dl>
        ) : null}
      </section>

      <OIInterestIncomeDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(incomeData) => {
          if (incomeData) {
            handleTotalCalculation(incomeData);
          }
          setShowAddFormCanvas(() => false);
        }}
        formValues={InterestIncomesDetails}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>{`You want to delete ${deleteMsg}.`}</p>}
        onYesBtnClick={() => {
          if (showDeleteModal) {
            handleTotalCalculation(showDeleteModal);
            setShowDeleteModal(() => null);
          }
        }}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default OIInterestIncomeDetails;

type RenderItemProps = {
  incomes: string[][];
  title: string;
  onDeleteBtnClick: () => void;
};

const RenderListItem: React.FC<RenderItemProps> = (props: RenderItemProps) => {
  const { incomes, title, onDeleteBtnClick } = props;

  let sum = 0;
  let hasValue = false;

  if (incomes?.length) {
    incomes.forEach((item) => {
      if (item[0] || item[1]) {
        hasValue = true;
      }

      if (item.length && item[1]) {
        sum += parseInt(item[1]);
      }
    });
  }

  return (
    <>
      {hasValue ? (
        <div className="eds-list--item">
          <dt>{title}</dt>
          <dd>
            <span>
              <PriceDisplay price={sum} />
            </span>
            <PreventEditComponent>
              <Button
                variant="transparent"
                className="icon-only"
                onClick={() => onDeleteBtnClick()}
              >
                <FASvgIcon icon={faTrash} />
              </Button>
            </PreventEditComponent>
          </dd>
        </div>
      ) : null}
    </>
  );
};
